import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PageHeader from '../../components/common/PageHeader';
import Card from '../../components/common/Card';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { MdDelete } from 'react-icons/md';
import { useAuth } from '../../context/AuthContext';
import useRole from '../../hooks/useRole';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs, getDoc, doc, addDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { friendlyDate, toLocalDateStr } from '../../utils/dateFormat';

const GradePage = () => {
  const { currentUser, userData } = useAuth();
  const { canGrade, canDelete, isStudent, isParent } = useRole();
  const isChildView = isStudent || isParent;
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('all');
  const [gradesByClass, setGradesByClass] = useState({});
  const [loading, setLoading] = useState(true);
  const [studentNames, setStudentNames] = useState({});

  // Add exam modal
  const [showModal, setShowModal] = useState(false);
  const [formClassId, setFormClassId] = useState('');
  const [formData, setFormData] = useState({});

  // Inline score editing
  const [expandedGrade, setExpandedGrade] = useState(null); // "classId-gradeId"
  const [scoreInputs, setScoreInputs] = useState({});

  useEffect(() => {
    if (!userData?.uid) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.uid]);

  const loadData = async () => {
    try {
      const uid = userData.uid;
      const lookupUid = isParent ? (userData.children?.[0] || uid) : uid;
      const classQ = isChildView
        ? query(collection(db, 'classes'), where('studentIds', 'array-contains', lookupUid))
        : query(collection(db, 'classes'), where('teacherId', '==', uid));
      const classSnap = await getDocs(classQ);
      const classList = classSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setClasses(classList);

      const gradesMap = {};
      const allSids = new Set();
      for (const cls of classList) {
        const gSnap = await getDocs(collection(db, 'classes', cls.id, 'grades'));
        gradesMap[cls.id] = gSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        (cls.studentIds || []).forEach(sid => allSids.add(sid));
      }
      setGradesByClass(gradesMap);

      const nameMap = {};
      for (const sid of allSids) {
        try {
          const userDoc = await getDoc(doc(db, 'users', sid));
          if (userDoc.exists()) nameMap[sid] = userDoc.data().name || sid;
          else nameMap[sid] = sid;
        } catch { nameMap[sid] = sid; }
      }
      setStudentNames(nameMap);
    } catch (err) {
      console.error('성적 로드 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStudentName = (sid) => studentNames[sid] || sid;

  const getClassGrades = () => {
    if (selectedClassId === 'all') {
      return classes.map(cls => ({ cls, grades: gradesByClass[cls.id] || [] }));
    }
    const cls = classes.find(c => c.id === selectedClassId);
    return cls ? [{ cls, grades: gradesByClass[cls.id] || [] }] : [];
  };

  const calcStats = (grade) => {
    const scores = Object.values(grade.scores || {}).map(Number).filter(v => !isNaN(v));
    if (scores.length === 0) return { avg: 0, max: 0, min: 0, count: 0 };
    return {
      avg: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length * 10) / 10,
      max: Math.max(...scores),
      min: Math.min(...scores),
      count: scores.length,
    };
  };

  const handleAddExam = async () => {
    if (!formClassId || !formData.examName) return;
    await addDoc(collection(db, 'classes', formClassId, 'grades'), {
      examName: formData.examName,
      date: formData.date || toLocalDateStr(new Date()),
      maxScore: Number(formData.maxScore) || 100,
      scores: {},
      createdBy: currentUser.uid,
      createdAt: serverTimestamp(),
    });
    setShowModal(false);
    setFormData({});
    loadData();
  };

  const handleDeleteGrade = async (classId, gradeId) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    await deleteDoc(doc(db, 'classes', classId, 'grades', gradeId));
    loadData();
  };

  const expandKey = (classId, gradeId) => `${classId}-${gradeId}`;

  const initInputs = (grade, studentIds) => {
    const inputs = {};
    studentIds.forEach(sid => {
      const s = (grade.scores || {})[sid];
      inputs[sid] = s !== undefined && s !== null ? String(s) : 'absent';
    });
    setScoreInputs(inputs);
  };

  const handleSaveScores = async (classId, gradeId) => {
    const scores = {};
    Object.entries(scoreInputs).forEach(([sid, val]) => {
      if (val !== '' && val !== 'absent') scores[sid] = Number(val);
    });
    await updateDoc(doc(db, 'classes', classId, 'grades', gradeId), { scores });
    setExpandedGrade(null);
    loadData();
  };

  const classGrades = getClassGrades();

  return (
    <Container>
      <PageHeader
        title={isChildView ? (isParent ? '자녀 성적' : '내 성적') : '시험 관리'}
        rightAction={canGrade ? <AddBtn onClick={() => setShowModal(true)}>+ 시험 추가</AddBtn> : null}
      />

      <FilterRow>
        <FilterBtn $active={selectedClassId === 'all'} onClick={() => setSelectedClassId('all')}>전체</FilterBtn>
        {classes.map(c => (
          <FilterBtn key={c.id} $active={selectedClassId === c.id} onClick={() => setSelectedClassId(c.id)}>
            {c.name}
          </FilterBtn>
        ))}
      </FilterRow>

      <Content>
        {loading ? (
          <LoadingSpinner />
        ) : classGrades.every(cg => cg.grades.length === 0) ? (
          <EmptyText>등록된 시험이 없습니다.</EmptyText>
        ) : (
          classGrades.map(({ cls, grades }) => (
            <ClassSection key={cls.id}>
              {selectedClassId === 'all' && <SectionTitle>{cls.name}</SectionTitle>}
              {grades.map(grade => {
                const stats = calcStats(grade);
                const studentIds = cls.studentIds || [];
                const key = expandKey(cls.id, grade.id);
                const isExpanded = expandedGrade === key;

                return (
                  <Card key={grade.id} $mb="12px" $padding="16px">
                    <ExamHeader>
                      <div>
                        <ExamName>{grade.examName}</ExamName>
                        <ExamMeta>날짜: {friendlyDate(grade.date)} · 만점: {grade.maxScore || 100}점</ExamMeta>
                      </div>
                      {canDelete && (
                        <DeleteBtn onClick={() => handleDeleteGrade(cls.id, grade.id)}><MdDelete size={18} /></DeleteBtn>
                      )}
                    </ExamHeader>
                    {isChildView ? (
                      /* 학생/학부모: 내 점수(자녀 점수) + 전체 통계 */
                      (() => {
                        const targetUid = isParent ? (userData?.children?.[0]) : userData?.uid;
                        const myScore = (grade.scores || {})[targetUid];
                        return (
                          <StatsGrid>
                            <StatItem $bg="var(--color-primary-light)">
                              <StatValue style={{ color: 'var(--color-primary)' }}>{myScore !== undefined ? myScore : '-'}</StatValue>
                              <StatLabel>내 점수</StatLabel>
                            </StatItem>
                            <StatItem $bg="#ECFDF5">
                              <StatValue>{stats.avg}</StatValue>
                              <StatLabel>평균</StatLabel>
                            </StatItem>
                            <StatItem $bg="#FFF1F2">
                              <StatValue>{stats.max}</StatValue>
                              <StatLabel>최고</StatLabel>
                            </StatItem>
                            <StatItem $bg="#EFF6FF">
                              <StatValue>{stats.count}명</StatValue>
                              <StatLabel>응시</StatLabel>
                            </StatItem>
                          </StatsGrid>
                        );
                      })()
                    ) : (
                      /* 강사: 전체 통계 + 점수 입력 */
                      <>
                        <StatsGrid>
                          <StatItem $bg="var(--color-primary-light)">
                            <StatValue>{stats.avg}</StatValue>
                            <StatLabel>평균</StatLabel>
                          </StatItem>
                          <StatItem $bg="#ECFDF5">
                            <StatValue>{stats.max}</StatValue>
                            <StatLabel>최고</StatLabel>
                          </StatItem>
                          <StatItem $bg="#FFF1F2">
                            <StatValue>{stats.min}</StatValue>
                            <StatLabel>최저</StatLabel>
                          </StatItem>
                          <StatItem $bg="#EFF6FF">
                            <StatValue>{stats.count}/{studentIds.length}</StatValue>
                            <StatLabel>응시</StatLabel>
                          </StatItem>
                        </StatsGrid>
                        {canGrade && (
                          <div style={{ marginTop: 8 }}>
                            <ExpandChip
                              $active={isExpanded}
                              onClick={() => {
                                if (!isExpanded) initInputs(grade, studentIds);
                                setExpandedGrade(isExpanded ? null : key);
                              }}
                            >
                              학생별 점수 {isExpanded ? '▲' : '▼'}
                            </ExpandChip>
                          </div>
                        )}
                        {isExpanded && (
                          <ScoreSection>
                            {studentIds.map(sid => {
                              const val = scoreInputs[sid] ?? 'absent';
                              const isAbsent = val === 'absent';
                              return (
                                <ScoreRow key={sid}>
                                  <span>{getStudentName(sid)}</span>
                                  <ScoreInputGroup>
                                    <ScoreInput
                                      type="number"
                                      placeholder=""
                                      value={isAbsent ? '' : val}
                                      disabled={isAbsent}
                                      onChange={e => setScoreInputs(p => ({ ...p, [sid]: e.target.value }))}
                                    />
                                    <AbsentBtn
                                      $active={isAbsent}
                                      onClick={() => setScoreInputs(p => ({ ...p, [sid]: isAbsent ? '' : 'absent' }))}
                                    >미응시</AbsentBtn>
                                  </ScoreInputGroup>
                                </ScoreRow>
                              );
                            })}
                            <SaveBtnWrap>
                              <SaveBtn onClick={() => handleSaveScores(cls.id, grade.id)}>저장</SaveBtn>
                            </SaveBtnWrap>
                          </ScoreSection>
                        )}
                      </>
                    )}
                  </Card>
                );
              })}
            </ClassSection>
          ))
        )}
      </Content>

      {showModal && (
        <ModalOverlay onClick={() => setShowModal(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>시험 일정 추가</ModalTitle>
              <CloseBtn onClick={() => setShowModal(false)}>✕</CloseBtn>
            </ModalHeader>
            <ModalBody>
              <FieldLabel>클래스 선택 *</FieldLabel>
              <FieldSelect value={formClassId} onChange={e => setFormClassId(e.target.value)}>
                <option value="">선택하세요</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </FieldSelect>
              <FieldLabel>시험 이름 *</FieldLabel>
              <FieldInput
                placeholder=""
                value={formData.examName || ''}
                onChange={e => setFormData(p => ({ ...p, examName: e.target.value }))}
              />
              <FieldLabel>날짜</FieldLabel>
              <FieldInput
                type="date"
                value={formData.date || ''}
                onChange={e => setFormData(p => ({ ...p, date: e.target.value }))}
              />
              <FieldLabel>만점</FieldLabel>
              <FieldInput
                type="number"
                placeholder=""
                value={formData.maxScore || ''}
                onChange={e => setFormData(p => ({ ...p, maxScore: e.target.value }))}
              />
              <ModalButtons>
                <CancelBtn onClick={() => setShowModal(false)}>취소</CancelBtn>
                <SubmitBtn onClick={handleAddExam}>등록</SubmitBtn>
              </ModalButtons>
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default GradePage;

const Container = styled.div`
  background: ${({ theme }) => theme.colors.background.default};
  min-height: 100vh;
  padding-bottom: 80px;
`;

const AddBtn = styled.button`
  padding: 6px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  border: 1px solid rgba(255,255,255,0.5);
  color: white;
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const FilterRow = styled.div`
  display: flex;
  padding: 4px 16px 0;
  gap: 24px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: 16px;
  &::-webkit-scrollbar { display: none; }
`;

const FilterBtn = styled.button`
  padding: 12px 0;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ $active }) => $active ? 700 : 400};
  border: none;
  border-bottom: 2px solid ${({ $active, theme }) => $active ? theme.colors.primary : 'transparent'};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.text.hint};
  background: none;
  white-space: nowrap;
  flex-shrink: 0;
  margin-bottom: -1px;
`;

const Content = styled.div`
  padding: 0 16px 16px;
`;

const ClassSection = styled.div`
  margin-bottom: 24px;
  padding-bottom: 24px;
  margin-left: -16px;
  margin-right: -16px;
  padding-left: 16px;
  padding-right: 16px;
  border-bottom: 4px solid #F0F0F0;
  &:last-child { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
`;

const SectionTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  margin-bottom: 12px;
`;

const ExamHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const ExamName = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 600;
`;

const ExamMeta = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.hint};
  margin-top: 2px;
`;

const DeleteBtn = styled.button`
  background: none;
  border: none;
  color: #9CA3AF;
  cursor: pointer;
  padding: 4px;
`;

const StatsGrid = styled.div`
  display: flex;
  gap: 8px;
`;

const StatItem = styled.div`
  flex: 1;
  text-align: center;
  padding: 8px;
  background: ${({ $bg }) => $bg || '#F5F7FA'};
  border-radius: 8px;
`;

const StatValue = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
`;

const StatLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 2px;
`;

const EmptyText = styled.p`
  text-align: center;
  padding: 40px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const ExpandChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  background: ${({ $active }) => $active ? 'var(--color-primary)' : 'var(--color-primary-light)'};
  color: ${({ $active }) => $active ? 'white' : 'var(--color-primary)'};
`;

const ScoreSection = styled.div`
  margin-top: 10px;
  border-top: 1px solid #F3F4F6;
  padding-top: 8px;
`;

const ScoreRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  span { flex: 1; font-size: 14px; }
`;

const ScoreInputGroup = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;
`;

const ScoreInput = styled.input`
  width: 100px;
  padding: 7px;
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  text-align: center;
  font-size: 15px;
  &:disabled { background: #F3F4F6; color: #9CA3AF; }
`;

const AbsentBtn = styled.button`
  padding: 7px 14px;
  border: 1px solid ${({ $active }) => $active ? '#EF4444' : '#D1D5DB'};
  background: ${({ $active }) => $active ? '#FEF2F2' : 'white'};
  color: ${({ $active }) => $active ? '#EF4444' : '#6B7280'};
  border-radius: 8px;
  font-size: 13px;
  font-weight: ${({ $active }) => $active ? 600 : 400};
  cursor: pointer;
  white-space: nowrap;
`;

const SaveBtnWrap = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0 12px;
  margin-top: 12px;
  margin-bottom: 8px;
`;

const SaveBtn = styled.button`
  padding: 8px 24px;
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`;

// Modal
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 200;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const Modal = styled.div`
  width: 100%;
  max-width: 480px;
  background: white;
  border-radius: 20px 20px 0 0;
  max-height: 85vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: var(--color-primary-light);
  border-bottom: 1px solid #D9E2FF;
`;

const ModalTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
  color: #1E3A5F;
`;

const CloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.text.primary};
  background: white;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin-bottom: 6px;
  margin-top: 14px;
`;

const FieldInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const FieldSelect = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const CancelBtn = styled.button`
  flex: 1;
  padding: 14px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const SubmitBtn = styled.button`
  flex: 2;
  padding: 14px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: ${({ theme }) => theme.fontSize.md};
`;
